import RestaurantCard from "@/components/RestaurantCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  getRestaurantDetails,
  resetRestaurantDetailsState,
  restaurantDetailsState,
} from "@/store/restaurantDetailsState";
import {
  Activity,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
} from "react";
import { generatePath, useNavigate, useParams } from "react-router";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import Text from "@/components/Text";
import RestaurantMainInformation from "@/components/RestaurantMainInformation";
import RestaurantCompare from "@/components/RestaurantCompare";
import RestaurantKrunshScore from "@/components/RestaurantKrunshScore";
import RestaurantRanking from "@/components/RestaurantRanking";
import { Button } from "@/components/ui/button";
import {
  BikeIcon,
  InfoIcon,
  Settings2,
  ShoppingCart,
  StarIcon,
  Tag,
  Trash2Icon,
  Lock,
} from "lucide-react";
import RemoveRestaurantDialog from "@/components/dialogs/RemoveRestaurantDialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RestaurantMenu from "@/components/RestaurantMenu";
import RestaurantPromotion from "@/components/RestaurantPromotion";
import {
  resetRemoveUserRestaurantsState,
  userRemoveRestaurantState,
} from "@/store/userRemoveRestaurantState";
import { toast } from "sonner";
import { NearbyRadius, type RestaurantLeaderboardEntry } from "@/API";
import Badge from "@/components/Badge";
import { useTranslation } from "react-i18next";
import { getColorKrunshScore, triggerEvent } from "@/lib/utils";
import useDragAndScroll from "@/hooks/useDragAndScroll";
import RestaurantEvolution from "@/components/RestaurantEvolution";
import { entitlementsState } from "@/store/entitlementsState";
import { setActionTriggerNeedCore } from "@/store/restrictedFeatureState";
import { userPlanState } from "@/store/userPlanState";

const getBg = (value: number): string => {
  if (value < 20) return "#FFF1F1";
  if (value < 80) return "#FFF3EB";
  return "#DEFBE6";
};

const getBorder = (value: number): string => {
  if (value < 40) return "#E2483D";
  if (value < 80) return "#E56910";
  return "#198038";
};

const getTitle = (value: number): string => {
  if (value < 40) return "restaurant_public_title_low_score";
  if (value < 80) return "restaurant_public_title_middle_score";
  return "restaurant_public_title_hight_score";
};

const getDescription = (value: number): string[] => {
  if (value < 40)
    return [
      "restaurant_public_description_1_low_score",
      "restaurant_public_description_2_low_score",
      "restaurant_public_description_3_low_score",
    ];
  if (value < 80)
    return [
      "restaurant_public_description_1_middle_score",
      "restaurant_public_description_2_middle_score",
      "restaurant_public_description_3_middle_score",
    ];
  return [
    "restaurant_public_description_1_high_score",
    "restaurant_public_description_2_high_score",
    "restaurant_public_description_3_high_score",
  ];
};

const getImpact = (value: number): string => {
  if (value < 40) return "restaurant_public_impact_low_score";
  if (value < 80) return "restaurant_public_impact_middle_score";
  return "restaurant_public_impact_hight_score";
};

export type ViewMode =
  | "general"
  | "krunsh-score"
  | "ranking"
  | "compare"
  | "menu"
  | "promotion";

const Restaurant: FC = () => {
  const navigate = useNavigate();
  const { sm } = useDetectSizeDevice();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId?: string }>();
  const { isLoaded } = useJsApiLoader({
    id: "55f6b0cbd2ae313dcf7f2bce",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
  });
  const refElementToScroll = useRef<HTMLDivElement>(null);
  const { onMouseDown, onMouseLeave, onMouseMoveCapture, onMouseUp } =
    useDragAndScroll({
      elementToScroll: refElementToScroll,
      direction: "horizontal",
    });

  const { data: restaurant, isLoading, isFetching } = restaurantDetailsState();
  const { data: restaurantIsRemoved } = userRemoveRestaurantState();
  const { data: entitlements } = entitlementsState();
  const { plan } = userPlanState();

  const [viewMode, setViewMode] = useState<ViewMode>("krunsh-score");
  const [dialogToRemoveRestaurantOpen, setDialogToRemoveRestaurantOpen] =
    useState(false);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [positionOverLay, setPositionOverlay] = useState({
    lat: 48.858374,
    lng: 2.336046,
  });
  const [restaurantSelected, setRestaurantSelected] =
    useState<null | RestaurantLeaderboardEntry>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (restaurantId) {
      getRestaurantDetails({
        id: restaurantId,
        nearbyRadius: plan
          ? NearbyRadius.TWO_KILOMETERS
          : NearbyRadius.TWO_HUNDRED_METERS,
      });
    }

    return () => {
      resetRestaurantDetailsState();
    };
  }, [restaurantId]);

  useEffect(() => {
    if (restaurantIsRemoved) {
      toast.success("Restaurant retiré", {
        duration: 2000,
      });
      resetRemoveUserRestaurantsState();
      navigate("/");
    }
  }, [restaurantIsRemoved]);

  useEffect(() => {
    if (restaurant && map) {
      map.setCenter({
        lat: restaurant.position.latitude,
        lng: restaurant.position.longitude,
      });
    }
  }, [restaurant, map]);

  if (isLoading)
    return (
      <div className="flex gap-[24px] w-[100%] p-[16px] sm:p-[40px]">
        <Skeleton className="h-[270px] w-[260px] rounded-[12px]" />
        <Skeleton className="h-[270px] w-[100%] rounded-[12px]" />
      </div>
    );

  const fullRestaurantDetailsActivated = Boolean(
    entitlements?.find(({ name }) => name === "full_restaurant_details"),
  );

  if (!restaurant) return null;

  return (
    <>
      {restaurant && (
        <RemoveRestaurantDialog
          open={dialogToRemoveRestaurantOpen}
          onClose={() => setDialogToRemoveRestaurantOpen(false)}
          restaurant={restaurant}
        />
      )}

      <div
        style={{
          height: sm
            ? "calc(100dvh - var(--height-header))"
            : "calc(100dvh - var(--height-header) - var(--height-header-not-sm))",
        }}
        className={
          "flex flex-col bg-[#F7F8F9] w-[100%] p-[16px] sm:p-[24px] 2xl:px-[120px] overflow-y-auto overflow-x-hidden"
        }
      >
        <div className="flex justify-between mb-[24px]">
          <Button
            onClick={() => {
              history.back();
            }}
            variant="secondaryOutlined"
            className="w-[min-content]"
          >
            {t("back")}
          </Button>
          <Button
            variant="warningOutlined"
            onClick={() => {
              setDialogToRemoveRestaurantOpen(true);
            }}
          >
            <Trash2Icon />
            {t("delete_this_restaurant")}
          </Button>
        </div>
        <div className="flex gap-[24px] flex-col sm:flex-row">
          <div className="w-[100%] sm:w-[264px]">
            <RestaurantCard
              firstSeen={restaurant.firstSeenAt}
              deliveryTime={restaurant.deliveryTime}
              name={restaurant.name}
              address={restaurant?.address ?? ""}
              tags={
                restaurant?.categories?.filter(
                  (it) => typeof it === "string",
                ) ?? []
              }
              rate={restaurant?.rating ?? ""}
              image={restaurant?.imageUrl ?? undefined}
              platform={restaurant.platform.toLowerCase()}
              krunshScore={restaurant.krunshScore}
              linkToRestaurant={restaurant?.url ?? undefined}
            />
          </div>

          {isLoaded && (
            <GoogleMap
              zoom={16}
              onLoad={onLoad}
              onUnmount={onUnmount}
              onClick={() => {
                setRestaurantSelected(null);
                setOpenOverlay(false);
              }}
              clickableIcons={false}
              mapContainerStyle={{
                width: "100%",
                borderRadius: 12,
                height: sm ? "auto" : 300,
              }}
              options={{
                fullscreenControl: false,
                mapTypeControl: false,
              }}
            >
              <Marker
                icon="/img/pins/store.svg"
                position={{
                  lat: restaurant.position.latitude,
                  lng: restaurant.position.longitude,
                }}
              />
              {restaurant.nearbyRestaurants.restaurants.map(
                (nearbyRestaurant) => {
                  if (!nearbyRestaurant) return null;

                  return (
                    <Marker
                      key={nearbyRestaurant.id}
                      icon={
                        restaurant.promotionCount > 0
                          ? "/img/pins/store-ranked-promo.svg"
                          : "/img/pins/store-ranked.svg"
                      }
                      position={{
                        lat: nearbyRestaurant.position.latitude,
                        lng: nearbyRestaurant.position.longitude,
                      }}
                      onClick={() => {
                        setRestaurantSelected(nearbyRestaurant);
                        setPositionOverlay({
                          lat: nearbyRestaurant.position.latitude,
                          lng: nearbyRestaurant.position.longitude,
                        });
                        setOpenOverlay(true);
                        triggerEvent("Map Pin Clicked", {
                          store_id: nearbyRestaurant.id,
                        });
                        if (map) {
                          map.setCenter({
                            lat: nearbyRestaurant.position.latitude,
                            lng: nearbyRestaurant.position.longitude,
                          });
                        }
                      }}
                      clickable={true}
                    />
                  );
                },
              )}

              {openOverlay && (
                <OverlayView
                  position={positionOverLay}
                  mapPaneName={"floatPane"}
                >
                  <div
                    className="bg-[#FFFFFF] rounded-[12px] py-[16px] px-[24px] flex flex-col w-[300px] cursor:pointer"
                    onClick={() => {
                      if (restaurantSelected)
                        navigate(
                          generatePath("/explorer/restaurant/:restaurantId", {
                            restaurantId: restaurantSelected.id,
                          }),
                        );
                    }}
                  >
                    {restaurantSelected && (
                      <>
                        <div className="flex gap-[12px] items-center">
                          <img
                            className="h-[40px] w-[40px] rounded-[3px]"
                            src={restaurantSelected?.imageUrl ?? undefined}
                            alt={`image of ${restaurantSelected.name}`}
                          />
                          <div className="flex flex-col gap-[4px]">
                            <Text fontWeight={600} fontSize={14}>
                              {restaurantSelected.name}
                            </Text>
                            <Text fontWeight={400} fontSize={12}>
                              {restaurantSelected.address}
                            </Text>
                          </div>
                        </div>
                        <div className="flex items-center gap-[8px] mt-[12px]">
                          <img
                            alt={`icon of ${restaurantSelected.platform}`}
                            src={`/partner/${restaurantSelected.platform.toLowerCase()}.png`}
                            className="w-[24px] h-[24px] rounded-[54px]"
                          />
                          <div
                            className="bg-[white] ml-[-20px] h-[24px] w-[24px] rounded-[50%] flex items-center justify-center"
                            style={{
                              border: `2px solid ${getColorKrunshScore(restaurantSelected.krunshScore)}`,
                            }}
                          >
                            <Text
                              fontSize={12}
                              fontWeight={600}
                              color={getColorKrunshScore(
                                restaurantSelected.krunshScore,
                              )}
                            >
                              {restaurantSelected.krunshScore}
                            </Text>
                          </div>
                          <Badge>
                            <StarIcon height={16} width={16} color="#758195" />
                            <Text fontWeight={600} fontSize={14}>
                              {restaurantSelected.rating}
                            </Text>
                          </Badge>
                          <Badge>
                            <BikeIcon height={16} width={16} color="#758195" />
                            <Text
                              fontWeight={600}
                              fontSize={14}
                            >{`${restaurantSelected.deliveryTime} min`}</Text>
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>
                </OverlayView>
              )}
            </GoogleMap>
          )}
        </div>

        {restaurantId && (
          <div className="mt-[44px]">
            <RestaurantEvolution restaurantId={restaurantId} />
          </div>
        )}

        <div
          ref={refElementToScroll}
          onMouseDown={() => onMouseDown()}
          onMouseLeave={() => onMouseLeave()}
          onMouseUp={() => onMouseUp()}
          onMouseMoveCapture={(e) =>
            onMouseMoveCapture(e as unknown as MouseEvent)
          }
          className="flex h-[48px] min-h-[48px] items-center mt-[40px] mb-[40px] overflow-x-auto overflow-y-auto"
        >
          <div
            onClick={() => {
              setViewMode("krunsh-score");
              triggerEvent("Score Viewed");
            }}
            style={
              viewMode === "krunsh-score"
                ? { borderBottom: "3px solid #1D7AFC" }
                : { border: "3px solid transparent" }
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <img height={22} width={22} src="/svg/k.svg" />
              </TooltipTrigger>
              <TooltipContent>{t("krunsh_score_description")}</TooltipContent>
            </Tooltip>
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("krunsh_score")}
            </Text>
          </div>
          <div
            onClick={() => {
              setViewMode("ranking");
              triggerEvent("Ranking Viewed");
            }}
            style={
              viewMode === "ranking"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <img height={18} width={18} src="/svg/ranking.svg" />
              </TooltipTrigger>
              <TooltipContent>{t("ranking_description")}</TooltipContent>
            </Tooltip>
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("ranking")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("menu");
                triggerEvent("Menu Viewed");
              } else {
                setActionTriggerNeedCore(true);
              }
            }}
            style={
              viewMode === "menu"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <ShoppingCart height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("menu_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}

            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("menu")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("promotion");
                triggerEvent("Promotion Viewed");
              } else {
                setActionTriggerNeedCore(true);
              }
            }}
            style={
              viewMode === "promotion"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Tag height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("promotion_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}

            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("promotions")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("compare");
                triggerEvent("Compared Viewed");
              } else {
                setActionTriggerNeedCore(true);
              }
            }}
            style={
              viewMode === "compare"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Settings2 height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>{t("compare_description")}</TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}

            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("compare")}
            </Text>
          </div>
          <div
            onClick={() => {
              if (fullRestaurantDetailsActivated) {
                setViewMode("general");
                triggerEvent("General Information Viewed");
              } else {
                setActionTriggerNeedCore(true);
              }
            }}
            style={
              viewMode === "general"
                ? { borderBottom: "3px solid #1D7AFC" }
                : undefined
            }
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            {fullRestaurantDetailsActivated ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon height={18} width={18} color="#44546F" />
                </TooltipTrigger>
                <TooltipContent>
                  {t("general_information_description")}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Lock color="#44546F" height={18} width={18} />
            )}

            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("general_information")}
            </Text>
          </div>
        </div>

        {viewMode === "krunsh-score" && (
          <div
            className="w-[100%] p-[24px] rounded-[8px] mb-[24px] flex flex-col gap-[8px]"
            style={{
              backgroundColor: getBg(restaurant.krunshScore),
              border: `solid 1px ${getBorder(restaurant.krunshScore)}`,
            }}
          >
            <Text fontSize={14} fontWeight={600} className="mb-[4px]">
              {t(getTitle(restaurant.krunshScore))}
            </Text>
            {getDescription(restaurant.krunshScore).map((text) => (
              <Text fontSize={14} fontWeight={400} key={text}>
                {t(text)}
              </Text>
            ))}
            <Text fontSize={14} fontWeight={600} className="mt-[4px]">
              {t(getImpact(restaurant.krunshScore))}
            </Text>
          </div>
        )}

        <Activity mode={viewMode === "general" ? "visible" : "hidden"}>
          <RestaurantMainInformation
            restaurant={restaurant ?? undefined}
            isLoading={isLoading}
          />
        </Activity>
        <Activity mode={viewMode === "compare" ? "visible" : "hidden"}>
          <RestaurantCompare restaurant={restaurant} />
        </Activity>
        <Activity mode={viewMode === "krunsh-score" ? "visible" : "hidden"}>
          <RestaurantKrunshScore
            restaurant={restaurant ?? undefined}
            isLoading={isLoading}
            isBlured={!fullRestaurantDetailsActivated}
          />
        </Activity>
        <Activity mode={viewMode === "ranking" ? "visible" : "hidden"}>
          <RestaurantRanking
            isFetching={isFetching}
            isLoading={isLoading}
            restaurant={restaurant ?? undefined}
          />
        </Activity>
        <Activity mode={viewMode === "menu" ? "visible" : "hidden"}>
          <RestaurantMenu restaurant={restaurant} />
        </Activity>
        <Activity mode={viewMode === "promotion" ? "visible" : "hidden"}>
          <RestaurantPromotion restaurant={restaurant ?? null} />
        </Activity>
      </div>
    </>
  );
};

export default Restaurant;
